import {
  budgetBands,
  comfortStyles,
  intakeSources,
  tripInterests,
  type BudgetBand,
  type ComfortStyle,
  type IntakeSource,
  type MessageKey,
  type TripInterest,
} from '@itin/shared'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState, type FormEvent, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { Logo } from '../components/Logo.js'
import { Button, ErrorNotice, Field, Input, TextArea } from '../components/ui.js'
import { api } from '../lib/api.js'
import { useLocale, useT } from '../lib/i18n.js'

/**
 * The public intake form -- what a prospective client sees after the first
 * WhatsApp conversation with Ana.
 *
 * Deliberately not a long scrolling form: five short steps with a progress
 * line, one decision per screen on a phone, which is where nearly every client
 * arrives from. It borrows the look of her website (cream ground, gold rules,
 * display serif) so that opening the link feels like staying inside her brand
 * rather than being handed to a form vendor.
 *
 * No account, no cookie and nothing in this file knows an agency except the id
 * in the URL. The visitor's only power is to submit; see migration 0025.
 */

type Draft = {
  name: string
  whatsapp: string
  email: string
  destination: string
  whenText: string
  durationDays: string
  adults: number
  children: number
  childrenAges: string
  comfort: ComfortStyle | null
  budgetBand: BudgetBand | null
  lastTrip: string
  interests: TripInterest[]
  notes: string
  source: IntakeSource | null
  consent: boolean
  website: string
}

const EMPTY: Draft = {
  name: '',
  whatsapp: '',
  email: '',
  destination: '',
  whenText: '',
  durationDays: '',
  adults: 2,
  children: 0,
  childrenAges: '',
  comfort: null,
  budgetBand: null,
  lastTrip: '',
  interests: [],
  notes: '',
  source: null,
  consent: false,
  website: '',
}

const STEPS = ['who', 'trip', 'style', 'invest', 'finish'] as const

export function IntakePage() {
  const t = useT()
  const { locale } = useLocale()
  const { agencyId = '' } = useParams()
  const [step, setStep] = useState(0)
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [done, setDone] = useState(false)

  const page = useQuery({
    queryKey: ['intake-public', agencyId],
    queryFn: () => api.getIntakePublic(agencyId),
    retry: false,
  })

  const send = useMutation({
    mutationFn: () =>
      api.submitIntake(agencyId, {
        name: draft.name,
        whatsapp: draft.whatsapp,
        email: draft.email.trim() || null,
        destination: draft.destination,
        whenText: draft.whenText,
        durationDays: draft.durationDays ? Number(draft.durationDays) : null,
        adults: draft.adults,
        children: draft.children,
        childrenAges: draft.children > 0 ? draft.childrenAges.trim() || null : null,
        comfort: draft.comfort!,
        budgetBand: draft.budgetBand!,
        lastTrip: draft.lastTrip.trim() || null,
        interests: draft.interests,
        notes: draft.notes.trim() || null,
        source: draft.source,
        consent: true,
        locale,
        website: draft.website,
      }),
    onSuccess: () => {
      setDone(true)
      window.scrollTo({ top: 0 })
    },
  })

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }))

  // Radios inside a step cannot use the browser's `required` across custom
  // cards, so the two choices that must be made block the step explicitly.
  const stepReady =
    (STEPS[step] === 'style' ? draft.comfort !== null : true) &&
    (STEPS[step] === 'invest' ? draft.budgetBand !== null : true) &&
    (STEPS[step] === 'finish' ? draft.consent : true)

  function onSubmit(event: FormEvent) {
    event.preventDefault()
    if (!stepReady) return
    if (step < STEPS.length - 1) {
      setStep(step + 1)
      window.scrollTo({ top: 0 })
    } else {
      send.mutate()
    }
  }

  if (page.isPending) return <Shell>{t('common.loading')}</Shell>
  if (page.isError) {
    return (
      <Shell>
        <p data-testid="intake-not-found" className="text-center text-slate-600">
          {t('intake.notFound')}
        </p>
      </Shell>
    )
  }

  if (done) {
    return (
      <Shell wide>
        <div data-testid="intake-done" className="flex flex-col gap-4 text-center">
          <h1 className="font-display text-3xl font-semibold">{t('intake.done.title')}</h1>
          <p className="text-slate-600">{t('intake.done.body')}</p>
        </div>
        {page.data.bookingUrl ? (
          <section className="flex flex-col gap-3">
            <h2 className="text-center font-display text-xl font-semibold">
              {t('intake.done.bookTitle')}
            </h2>
            {/*
              The scheduling page is hosted by Cal.com or Calendly, so the
              frame is sandboxed to what a booking widget needs and no more: it
              cannot navigate this page or reach its storage.
            */}
            <iframe
              data-testid="intake-booking"
              title={t('intake.done.bookTitle')}
              src={page.data.bookingUrl}
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              referrerPolicy="no-referrer"
              className="h-[640px] w-full rounded-lg border border-slate-200 bg-white"
            />
          </section>
        ) : (
          <p data-testid="intake-no-booking" className="text-center text-sm text-slate-500">
            {t('intake.done.noBooking')}
          </p>
        )}
      </Shell>
    )
  }

  const current = STEPS[step]!

  return (
    <Shell>
      <header className="flex flex-col items-center gap-2 text-center">
        <p className="font-label text-xs uppercase tracking-[0.25em] text-gold">
          {page.data.agencyName}
        </p>
        <h1 className="font-display text-3xl font-semibold leading-tight sm:text-4xl">
          {t('intake.title')}
        </h1>
        {step === 0 && <p className="max-w-prose text-sm text-slate-600">{t('intake.lead')}</p>}
      </header>

      {/* Progress: a line, not numbered circles -- it should feel like a journey, not a checkout. */}
      <div
        className="flex items-center gap-1.5"
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={STEPS.length}
        aria-valuenow={step + 1}
        aria-label={t(`intake.step.${current}` as MessageKey)}
      >
        {STEPS.map((s, i) => (
          <span
            key={s}
            className={`h-1 flex-1 rounded-full transition-colors ${i <= step ? 'bg-gold' : 'bg-slate-200'}`}
          />
        ))}
      </div>

      <form onSubmit={onSubmit} className="flex flex-col gap-5" data-testid="intake-form">
        <h2 className="font-display text-2xl font-semibold">{t(`intake.step.${current}` as MessageKey)}</h2>

        {current === 'who' && (
          <>
            <Field label={t('intake.name')}>
              <Input
                name="name"
                value={draft.name}
                autoComplete="name"
                required
                minLength={2}
                onChange={(e) => set('name', e.target.value)}
              />
            </Field>
            <Field label={t('intake.whatsapp')} hint={t('intake.whatsapp.hint')}>
              <Input
                name="whatsapp"
                type="tel"
                inputMode="tel"
                value={draft.whatsapp}
                autoComplete="tel"
                required
                pattern="[+()\d\s\-]{8,25}"
                onChange={(e) => set('whatsapp', e.target.value)}
              />
            </Field>
            <Field label={t('intake.email')}>
              <Input
                name="email"
                type="email"
                value={draft.email}
                autoComplete="email"
                onChange={(e) => set('email', e.target.value)}
              />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Counter
                label={t('intake.adults')}
                value={draft.adults}
                min={1}
                onChange={(v) => set('adults', v)}
                testId="adults"
              />
              <Counter
                label={t('intake.children')}
                value={draft.children}
                min={0}
                onChange={(v) => set('children', v)}
                testId="children"
              />
            </div>
            {draft.children > 0 && (
              <Field label={t('intake.childrenAges')} hint={t('intake.childrenAges.hint')}>
                <Input
                  name="childrenAges"
                  value={draft.childrenAges}
                  onChange={(e) => set('childrenAges', e.target.value)}
                />
              </Field>
            )}
          </>
        )}

        {current === 'trip' && (
          <>
            <Field label={t('intake.destination')} hint={t('intake.destination.hint')}>
              <Input
                name="destination"
                value={draft.destination}
                required
                minLength={2}
                onChange={(e) => set('destination', e.target.value)}
              />
            </Field>
            <Field label={t('intake.when')} hint={t('intake.when.hint')}>
              <Input
                name="whenText"
                value={draft.whenText}
                required
                onChange={(e) => set('whenText', e.target.value)}
              />
            </Field>
            <Field label={t('intake.duration')}>
              <Input
                name="durationDays"
                type="number"
                inputMode="numeric"
                min={1}
                max={120}
                value={draft.durationDays}
                onChange={(e) => set('durationDays', e.target.value)}
              />
            </Field>
          </>
        )}

        {current === 'style' && (
          <fieldset className="flex flex-col gap-3">
            <legend className="mb-1 text-sm font-medium text-slate-700">
              {t('intake.comfort.legend')}
            </legend>
            <p className="text-xs text-slate-500">{t('intake.comfort.hint')}</p>
            {comfortStyles.map((style) => (
              <Choice
                key={style}
                name="comfort"
                testId={`comfort-${style}`}
                checked={draft.comfort === style}
                onSelect={() => set('comfort', style)}
                title={t(`intake.comfort.${style}.title` as MessageKey)}
                description={t(`intake.comfort.${style}.desc` as MessageKey)}
              />
            ))}
          </fieldset>
        )}

        {current === 'invest' && (
          <>
            <p className="border-l-2 border-gold/50 pl-3 text-sm leading-relaxed text-slate-600">
              {t('intake.invest.lead')}
            </p>
            <Field label={t('intake.lastTrip')} hint={t('intake.lastTrip.hint')}>
              <TextArea
                name="lastTrip"
                rows={3}
                maxLength={2000}
                value={draft.lastTrip}
                onChange={(e) => set('lastTrip', e.target.value)}
              />
            </Field>
            <fieldset className="flex flex-col gap-2">
              <legend className="mb-1 text-sm font-medium text-slate-700">
                {t('intake.band.legend')}
              </legend>
              {budgetBands.map((band) => (
                <Choice
                  key={band}
                  name="budgetBand"
                  testId={`band-${band}`}
                  compact
                  checked={draft.budgetBand === band}
                  onSelect={() => set('budgetBand', band)}
                  title={t(`intake.band.${band}` as MessageKey)}
                />
              ))}
            </fieldset>
          </>
        )}

        {current === 'finish' && (
          <>
            <fieldset className="flex flex-col gap-2">
              <legend className="mb-1 text-sm font-medium text-slate-700">
                {t('intake.interests.legend')}
              </legend>
              <div className="flex flex-wrap gap-2">
                {tripInterests.map((interest) => {
                  const on = draft.interests.includes(interest)
                  return (
                    <button
                      key={interest}
                      type="button"
                      aria-pressed={on}
                      data-testid={`interest-${interest}`}
                      onClick={() =>
                        set(
                          'interests',
                          on
                            ? draft.interests.filter((i) => i !== interest)
                            : [...draft.interests, interest],
                        )
                      }
                      className={`min-h-11 rounded-full border px-4 text-sm transition-colors ${
                        on
                          ? 'border-gold bg-[#fdf6e3] text-slate-900'
                          : 'border-slate-300 bg-white text-slate-700 hover:border-gold'
                      }`}
                    >
                      {t(`intake.interest.${interest}` as MessageKey)}
                    </button>
                  )
                })}
              </div>
            </fieldset>

            <Field label={t('intake.notes')} hint={t('intake.notes.hint')}>
              <TextArea
                name="notes"
                rows={4}
                maxLength={2000}
                value={draft.notes}
                onChange={(e) => set('notes', e.target.value)}
              />
            </Field>

            <fieldset className="flex flex-col gap-2">
              <legend className="mb-1 text-sm font-medium text-slate-700">
                {t('intake.source.legend')}
              </legend>
              <div className="flex flex-wrap gap-2">
                {intakeSources.map((source) => (
                  <button
                    key={source}
                    type="button"
                    aria-pressed={draft.source === source}
                    onClick={() => set('source', draft.source === source ? null : source)}
                    className={`min-h-11 rounded-full border px-4 text-sm transition-colors ${
                      draft.source === source
                        ? 'border-gold bg-[#fdf6e3] text-slate-900'
                        : 'border-slate-300 bg-white text-slate-700 hover:border-gold'
                    }`}
                  >
                    {t(`intake.source.${source}` as MessageKey)}
                  </button>
                ))}
              </div>
            </fieldset>

            <label className="flex items-start gap-3 text-sm leading-snug text-slate-700">
              <input
                type="checkbox"
                data-testid="intake-consent"
                checked={draft.consent}
                onChange={(e) => set('consent', e.target.checked)}
                className="mt-0.5 h-5 w-5 shrink-0 accent-[#c9a84c]"
              />
              <span>{t('intake.consent')}</span>
            </label>

            {/* Honeypot: invisible to people, irresistible to scripts. */}
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={draft.website}
              onChange={(e) => set('website', e.target.value)}
              className="absolute -left-[9999px] h-0 w-0 opacity-0"
            />
          </>
        )}

        {send.isError && <ErrorNotice error={send.error} />}

        <div className="flex items-center justify-between gap-3 pt-1">
          {step > 0 ? (
            <Button type="button" variant="ghost" onClick={() => setStep(step - 1)}>
              {t('intake.back')}
            </Button>
          ) : (
            <span />
          )}
          <Button
            type="submit"
            variant="primary"
            data-testid="intake-next"
            disabled={!stepReady || send.isPending}
          >
            {send.isPending
              ? t('intake.sending')
              : step < STEPS.length - 1
                ? t('intake.next')
                : t('intake.submit')}
          </Button>
        </div>
      </form>
    </Shell>
  )
}

function Shell({ children, wide = false }: { children: ReactNode; wide?: boolean }) {
  return (
    <div className="min-h-dvh bg-cream">
      <main
        className={`mx-auto flex flex-col gap-6 px-4 py-8 sm:py-12 ${wide ? 'max-w-2xl' : 'max-w-xl'}`}
      >
        <div className="flex justify-center">
          <Logo variant="full" />
        </div>
        <div className="flex flex-col gap-6 rounded-xl border border-slate-200/80 bg-white p-5 shadow-[0_1px_3px_rgba(10,14,31,0.06)] sm:p-8">
          {children}
        </div>
      </main>
    </div>
  )
}

function Counter({
  label,
  value,
  min,
  onChange,
  testId,
}: {
  label: string
  value: number
  min: number
  onChange: (value: number) => void
  testId: string
}) {
  return (
    <div className="flex flex-col gap-1.5 text-sm">
      <span className="font-medium text-slate-700">{label}</span>
      <div className="flex items-center justify-between rounded-md border border-slate-300 bg-white">
        <button
          type="button"
          aria-label={`− ${label}`}
          disabled={value <= min}
          onClick={() => onChange(value - 1)}
          className="h-11 w-11 text-lg text-slate-600 disabled:opacity-30"
        >
          −
        </button>
        <span data-testid={`count-${testId}`} className="text-base font-medium tabular-nums">
          {value}
        </span>
        <button
          type="button"
          aria-label={`+ ${label}`}
          disabled={value >= 30}
          onClick={() => onChange(value + 1)}
          className="h-11 w-11 text-lg text-slate-600 disabled:opacity-30"
        >
          +
        </button>
      </div>
    </div>
  )
}

/** A radio dressed as a card. The real input stays in the DOM for keyboards and screen readers. */
function Choice({
  name,
  testId,
  checked,
  onSelect,
  title,
  description,
  compact = false,
}: {
  name: string
  testId: string
  checked: boolean
  onSelect: () => void
  title: string
  description?: string
  compact?: boolean
}) {
  return (
    <label
      className={`flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-gold ${
        checked ? 'border-gold bg-[#fdf6e3]' : 'border-slate-300 bg-white hover:border-gold'
      } ${compact ? 'min-h-11 items-center py-3' : ''}`}
    >
      <input
        type="radio"
        name={name}
        data-testid={testId}
        checked={checked}
        onChange={onSelect}
        className="mt-1 h-4 w-4 shrink-0 accent-[#c9a84c]"
      />
      <span className="flex flex-col gap-1">
        <span className="font-medium text-slate-900">{title}</span>
        {description && <span className="text-sm leading-snug text-slate-600">{description}</span>}
      </span>
    </label>
  )
}
